import type { SxProps, Theme } from "@mui/material";

export const getModernPageColors = (theme: Theme) => {
  const c = theme.palette.customColors;

  return {
    pageBg: c.cardBackground,
    surface: c.white,
    surfaceMuted: c.lightBlue,
    surfaceSoft: c.blueLightBackground,
    border: c.lightGray,
    borderSoft: c.lightGrayBorder,
    title: c.darkBlueText,
    subtitle: c.grayText,
    label: c.mediumGrayText,
    accent: c.darkBlue,
    accentPurple: c.purple,
    headerBg: c.lightGreyColor,
    filterBg: c.lightBlue,
    filterHover: c.hoverGray,
    shadow: `0 8px 24px ${c.shadowVeryLight}`,
    shadowSoft: `0 2px 8px ${c.shadowLightBorder}`,
    success: c.successGreen,
    error: c.errorRed,
    credit: c.EmeraldGreen,
    debit: c.errorRed,
    infoBg: c.infoLightBackground,
    infoBorder: c.infoLightBorder,
  };
};

export const modernPageShellSx = (theme: Theme): SxProps<Theme> => {
  const colors = getModernPageColors(theme);
  return {
    bgcolor: colors.pageBg,
    minHeight: "100vh",
    py: { xs: 1, md: 2 },
    px: { xs: 1, md: 2 },
  };
};

export const modernPageCardSx = (theme: Theme): SxProps<Theme> => {
  const colors = getModernPageColors(theme);
  return {
    p: { xs: 2, md: 3 },
    borderRadius: 3,
    border: `1px solid ${colors.border}`,
    boxShadow: colors.shadow,
    bgcolor: colors.surface,
  };
};

export const modernPageTitleSx = (theme: Theme): SxProps<Theme> => ({
  color: getModernPageColors(theme).title,
});

export const modernPageSubtitleSx = (theme: Theme): SxProps<Theme> => ({
  mt: 0.5,
  color: getModernPageColors(theme).subtitle,
  fontSize: { xs: "0.8rem", sm: "0.875rem" },
});

export const modernFilterPanelSx = (theme: Theme): SxProps<Theme> => {
  const colors = getModernPageColors(theme);
  return {
    mt: 2,
    borderRadius: 2,
    border: `1px solid ${colors.border}`,
    // bgcolor: colors.filterBg,
    overflow: "hidden",
    boxShadow: colors.shadowSoft,
  };
};

export const modernFilterPanelHeaderSx = (
  theme: Theme,
  expanded: boolean,
): SxProps<Theme> => {
  const colors = getModernPageColors(theme);
  return {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    px: 2,
    py: 1,
    cursor: "pointer",
    borderBottom: expanded ? `1px solid ${colors.border}` : "none",
    bgcolor: colors.surface,
    "&:hover": {
      bgcolor: colors.filterHover,
    },
  };
};

export const modernFilterPanelTitleSx = (theme: Theme): SxProps<Theme> => ({
  color: getModernPageColors(theme).title,
});

export const modernTablePaperSx = (theme: Theme): SxProps<Theme> => {
  const colors = getModernPageColors(theme);
  return {
    mt: 2,
    borderRadius: 2,
    border: `1px solid ${colors.border}`,
    boxShadow: colors.shadowSoft,
    overflow: "hidden",
    bgcolor: colors.surface,
  };
};

export const modernTableHeaderCellSx = (theme: Theme) => ({
  textAlign: "center" as const,
  fontSize: "0.75rem",
  fontWeight: 700,
  color: getModernPageColors(theme).title,
  backgroundColor: getModernPageColors(theme).headerBg,
  py: 1.5,
  px: 1,
  lineHeight: 1.2,
  borderBottom: `1px solid ${getModernPageColors(theme).border}`,
});

export const modernTableBodyCellSx = (theme: Theme) => ({
  textAlign: "center" as const,
  fontSize: "0.75rem",
  py: 1.1,
  px: 1,
  lineHeight: 1.35,
  color: getModernPageColors(theme).title,
  borderBottom: `1px solid ${getModernPageColors(theme).borderSoft}`,
});

export const modernTableRowSx = (theme: Theme) => ({
  "&:hover": {
    bgcolor: getModernPageColors(theme).surfaceSoft,
  },
  "&:last-child td": {
    borderBottom: 0,
  },
});

export const modernDrawerHeaderSx = (theme: Theme): SxProps<Theme> => {
  const c = theme.palette.customColors;
  return {
    px: { xs: 1.5, sm: 2.5 },
    py: { xs: 1.5, sm: 2 },
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 1,
    flexShrink: 0,
    borderBottom: `1px solid ${c.lightGray}`,
    background: `linear-gradient(135deg, ${c.white} 0%, ${c.lightBlue} 100%)`,
  };
};

export const modernDrawerBodySx = (): SxProps<Theme> => ({
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  WebkitOverflowScrolling: "touch",
  px: { xs: 1.5, sm: 2.5 },
  py: { xs: 1.5, sm: 2 },
  display: "flex",
  flexDirection: "column",
  gap: { xs: 1.5, sm: 2 },
});

export const modernSectionPaperSx = (theme: Theme): SxProps<Theme> => {
  const colors = getModernPageColors(theme);
  return {
    p: { xs: 1.5, sm: 2 },
    borderRadius: 2,
    border: `1px solid ${colors.border}`,
    bgcolor: colors.surface,
    boxShadow: colors.shadowSoft,
  };
};

export const modernSectionTitleSx = (theme: Theme): SxProps<Theme> => ({
  color: getModernPageColors(theme).title,
});

export const modernDetailLabelSx = (theme: Theme): SxProps<Theme> => ({
  minWidth: { sm: 148 },
  color: getModernPageColors(theme).subtitle,
  flexShrink: 0,
});

export const modernDetailValueSx = (theme: Theme): SxProps<Theme> => ({
  minWidth: 0,
  color: getModernPageColors(theme).title,
  flex: 1,
  wordBreak: "break-word",
});

export const modernEmptyStateSx = (theme: Theme): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: getModernPageColors(theme).subtitle,
  textAlign: "center",
  px: 2,
});
